import { useEffect, useState } from "react";
import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader/Index";
import "./style.css";
import { channelsViewAll } from "../../../redux/features/Notifications";
import ChannelViewallCard from "../../../components/Channels/ChannelViewallCard";
import TopNavComponent from "../../../components/_utils/CommonTopNav/TopNavComponent";
import useCustomSearchParams from "../../../utils/useCustomSearchParams";
import {
  GlobalConfiguration,
  ScreenSizeConfiguration,
} from "../../../utils/configurationProvider";
import { PaginationConfiguration } from "../../../utils/configurationProvider";
import { IntervalConfiguration } from "../../../utils/configurationProvider";
import PaginationComponent from "../../../components/_utils/PaginationComponent";
import ICONS from "../../../assets/Icons";
import PrimaryButton from "../../../components/_utils/CommonButtons/PrimaryButton";
import UseHandleScroll from "../../../hooks/useHandleScroll";
import CommonToast from "../../../components/_utils/CommonToast";
import { toast } from "react-toastify";
import TEXT from "../../../utils/globalText";

/**
 * ChannelViewAll
 *
 * Lists all notification channels (Google Chat, Microsoft Teams, Slack, Webhook)
 * with search, multi-filter (status, linkage status, date range, target provider),
 * and pagination.
 *
 *
 * @param {Object} props
 * @param {string} [props.targetProvider] - Provider slug (e.g. "slack") to lock
 *   results to, when this list is rendered in the context of a single provider.
 * @param {boolean} [props.popup] - Whether the component is rendered inside a
 *   popup/modal
 * @param {string} [props.providerName] - Display name for the provider,
 * @param {Function} [props.setIsChannelViewallOpen] - Setter to close the
 *   "view all" panel, passed through to child card component.
 * @param {Function} [props.setTargetProvider] - Setter for the parent's
 *   selected target provider
 * @param {Function} [props.setLoadingDetail] - Setter to toggle a loading state
 * @returns {JSX.Element}
 */
const ChannelViewAll = ({
  targetProvider,
  popup,
  providerName,
  notificationId,
  setIsChannelViewallOpen,
  setTargetProvider,
  setLoadingDetail,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownOptions = PaginationConfiguration.paginationOptions;
  const services = useSelector((state) => state?.refreshToken?.services) || []; // List of service/permission flags the logged-in user has
  const hasAccess =
    services?.includes("CChannel") || services?.includes("Admin");

  const lastScrollTop = useRef(GlobalConfiguration.lengthCheckZero);
  const [direction, setDirection] = useState("");

  // Tracks scroll direction (up/down) to toggle the sticky nav background.
  const { handleScroll } = UseHandleScroll(setDirection, lastScrollTop);

  // Seeds query params from router state (e.g. when navigated here with a
  // preset approval status) or falls back to page 1 at the default page size.
  const { searchParam, handleSearchParams, handleClearSearchParam } =
    useCustomSearchParams(
      location?.state &&
        location?.state?.approvalStatus &&
        location?.state?.approvalStatus !== "all"
        ? {
            status: location?.state?.approvalStatus,
            pageno: GlobalConfiguration.lengthCheckOne,
            pagesize: dropdownOptions[0],
          }
        : {
            pageno: GlobalConfiguration.lengthCheckOne,
            pagesize: dropdownOptions[0],
          },
    );

  const actionRestricted = () => {
    toast.dismiss();
    CommonToast({
      text: TEXT?.ACTION_RESTRICTED,
      icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
      variant: "var(--error)",
      btnProps: {
        isDisabled: false,
        btnText: "Open in full view",
        icon: ICONS?.REDIRECT,
        enabledTextColor: "var(--text-primary)",
        enabledBgColor: "var(--negative-bg)",
        handleFunction: () => {
          navigate(`/home/notification_dashboard/channel_view_all`);
        },
      },
      buttonNeeded: true,
    });
  };

  /**
   * Converts an ISO-like date string into a Unix timestamp (seconds).
   * Used to normalize the "date" query param (persisted as an ISO string)
   * back into the second-based timestamp format used by filter state.
   */
  const convertDate = (dateString) => {
    if (!dateString) return;
    const date = new Date(dateString);
    const timestamp = Math.floor(
      date.getTime() / IntervalConfiguration.intervalMedium,
    );

    return timestamp;
  };
  const filterRef = useRef(null);
  const scrollRef = useRef(null);
  // states variables

  const [currentStatus, setCurrentStatus] = useState(
    searchParam.get("status") || "",
  );
  const [currentLinkageStatus, setCurrentLinkageStatus] = useState(
    searchParam.get("linkage_status") || "",
  );
  const [channelProvider, setChannelProvider] = useState(
    searchParam.get("channel_provider") || "",
  );

  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [channelsViewAllData, setChannelsViewAllData] = useState([]);

  const [showInnerSearchBar, setShowInnerSearchBar] = useState(false);
  const [searchUserTextDebounce, setSearchUserTextDebounce] = useState("");

  const [currentPage, setCurrentPage] = useState(
    searchParam.get("pageno") || GlobalConfiguration.lengthCheckOne,
  );
  const [startDate, setStartDate] = useState(null);

  const [endDate, setEndDate] = useState(null);
  const [searchData, setSearchData] = useState(searchParam.get("search") || "");

  const [target, setTarget] = useState(searchParam.get("target") || "");

  const [startDateFilter, setStartDateFilter] = useState(
    convertDate(searchParam.get("date")?.split(",")?.[0]) || "",
  );

  const [endDateFilter, setEndDateFilter] = useState(
    convertDate(searchParam.get("date")?.split(",")?.[1]) || "",
  );

  const [inputData, setInputData] = useState([]);

  const [active, setActive] = useState("");
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [mainData, setMainData] = useState([]);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(
    searchParam.get("pagesize") || PaginationConfiguration.paginationSmall,
  );

  const [pageSize, setPageSize] = useState(
    searchParam.get("pagesize") || PaginationConfiguration.paginationSmall,
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Tracks whether the viewport is currently mobile-sized,
  // initialized synchronously from the current window width to avoid
  // a flash of incorrect layout on first render
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= ScreenSizeConfiguration.mobileScreen,
  );
  const dispatch = useDispatch();

  /**
   * Maps a human-readable channel display name (as shown in the "Target"
   * filter dropdown) to the provider slug expected by the API.
   *
   */
  const mapChannelNameDefaultSearch = (channelProvider) => {
    const data =
      [
        {
          name: "Google chat",
          provider: "gchat",
        },
        {
          name: "Microsoft teams",
          provider: "teams",
        },
        { name: "Webhook", provider: "generic" },
        { name: "Slack", provider: "slack" },
      ].filter((item) => item?.name === channelProvider) || "";

    return data?.[0]?.provider;
  };

  useEffect(() => {
    // Re-evaluate mobile status whenever the window is resized
    const handleResize = () => {
      setIsMobile(window.innerWidth <= ScreenSizeConfiguration.mobileScreen);
    };

    window.addEventListener("resize", handleResize);

    // Clean up the listener on unmount to avoid memory leaks / stale closures
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /**
   * Fetches the channel list for the current filter/pagination state and
   * updates `channelsViewAllData` / `mainData` accordingly. Triggered by the
   * effect below whenever any filter, search, or pagination value changes.
   */
  const fetchData = async () => {
    setLoading(true);
    // let queryParams = searchParmasData;
    let queryParams = {
      page_no: currentPage,
      page_size: pageSize,
    };

    // Builds the API query params differently depending on whether the
    // provider is locked via the `targetProvider` prop (popup mode) or
    // freely selectable via the "Target" filter (standalone mode).
    const addParamsBasedOnSelection = () => {
      if (notificationId) {
        queryParams.notification_id = notificationId;
      }
      if (targetProvider) {
        queryParams.provider = targetProvider;
        if (searchData !== "") {
          queryParams.name = searchData;
        }
        if (targetProvider) {
          queryParams.provider = targetProvider;
        }
        if (startDateFilter !== "" && endDateFilter !== "") {
          queryParams.start_date = formatDate(startDateFilter);
          queryParams.end_date = formatDate(endDateFilter);
        }

        if (currentStatus !== "") {
          if (currentStatus === "Active") {
            queryParams.is_active = true;
          } else {
            queryParams.is_active = false;
          }
        }

        if (currentLinkageStatus !== "") {
          if (currentLinkageStatus === "Pending") {
            queryParams.verification_status = "pending";
          } else if (currentLinkageStatus === "Success") {
            queryParams.verification_status = "verified";
          } else {
            queryParams.verification_status = "failed";
          }
        }
      } else {
        if (searchData !== "") {
          queryParams.name = searchData;
        }
        if (target) {
          queryParams.provider = target;
        }
        if (startDateFilter !== "" && endDateFilter !== "") {
          queryParams.start_date = formatDate(startDateFilter);
          queryParams.end_date = formatDate(endDateFilter);
        }

        if (currentStatus !== "") {
          if (currentStatus === "Active") {
            queryParams.is_active = true;
          } else {
            queryParams.is_active = false;
          }
        }

        if (currentLinkageStatus !== "") {
          if (currentLinkageStatus === "Pending") {
            queryParams.verification_status = "pending";
          } else if (currentLinkageStatus === "Success") {
            queryParams.verification_status = "verified";
          } else {
            queryParams.verification_status = "failed";
          }
        }
        if (channelProvider !== "") {
          queryParams.provider = mapChannelNameDefaultSearch(channelProvider);
        }
      }
    };

    addParamsBasedOnSelection();

    try {
      const response = await dispatch(channelsViewAll(queryParams));

      setChannelsViewAllData(response?.payload?.channels);

      if (response?.payload?.status) {
        setMainData(response?.payload);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Persists a filter/search value to the URL query string.
   */
  const handleSubmit = (key, value) => {
    handleSearchParams(key, value);
  };

  /**
   * Shared handler for the "Status", "Channel linkage", and "Target" filter
   * dropdowns: updates the relevant piece of state, syncs it to the URL, and
   * resets pagination back to page 1 since the result set changes.
   *
   */
  const getChannelStatus = (item, ele) => {
    if (ele === "linkage_status") {
      setCurrentLinkageStatus(item);
      handleSubmit("linkage_status", item);
      setCurrentPage(GlobalConfiguration.lengthCheckOne);
    } else if (ele === "status") {
      handleSubmit("status", item);
      setCurrentStatus(item);
      setCurrentPage(GlobalConfiguration.lengthCheckOne);
      // setIsStatusSelected(item !== "all");
    } else if (ele === "channel_provider") {
      setChannelProvider(item);
      handleSubmit("channel_provider", item);

      setCurrentPage(GlobalConfiguration.lengthCheckOne);
    }
  };

  const handleStatus = (status) => {
    getChannelStatus(status, "status");
  };
  const handleLinkageStatus = (status) => {
    getChannelStatus(status, "linkage_status");
  };

  const handleChannelProvider = (provider) => {
    getChannelStatus(provider, "channel_provider");
  };

  /**
   * Formats a Unix timestamp (seconds) into a "DD-MM-YYYY" string for the API.
   */
  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);

    return `${String(date.getDate()).padStart(2, "0")}-${String(
      date.getMonth() + 1,
    ).padStart(2, "0")}-${date.getFullYear()}`;
  };

  /**
   * Handles a change from the "Date range" calendar filter.
   * Stores both the raw `Date` objects (for the calendar UI) and the
   * derived Unix timestamps (for the API query) in state; clears both when
   * the range is reset.
   */
  const handleDateChange = (dates) => {
    if (!dates) {
      setStartDate(null);
      setEndDate(null);
      setStartDateFilter("");
      setEndDateFilter("");
      return;
    }

    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    if (start && end) {
      const startTimestamp = Math.floor(start.getTime() / 1000);
      const endTimestamp = Math.floor(end.getTime() / 1000);
      setStartDateFilter(startTimestamp);
      setEndDateFilter(endTimestamp);
    } else {
      setStartDateFilter("");
      setEndDateFilter("");
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  /**
   * Handles selecting a new page size from the pagination dropdown: syncs it
   * to the URL, updates state, resets to page 1, and closes the dropdown.
   */
  const handleSelect = (value) => {
    handleSubmit("pagesize", value);

    setSelectedValue(value);
    setPageSize(value);
    setCurrentPage(GlobalConfiguration.lengthCheckOne);

    toggleDropdown();
  };

  /**
   * Populates `inputData` for the top-nav's inline search/filter input based
   * on which filter ("active") the user currently has focused. For "Search"
   * it just commits the debounced search text; for "Target" it filters the
   * static provider list
   */
  const fetchFilterData = async () => {
    let data = [];
    setLoadingData(true);
    switch (active) {
      case "Search":
        setSearchData(searchUserTextDebounce);
        break;
      case "Target":
        data = [
          { name: "Google chat", provider: "gchat" },
          { name: "Slack", provider: "slack" },
          { name: "Microsoft teams", provider: "teams" },
          { name: "Custom Webhook", provider: "generic" },
        ]
          ?.filter((itm) => {
            if (
              itm?.name
                ?.toLowerCase()
                .includes(searchUserTextDebounce?.toLowerCase())
            )
              return itm;
          })
          .map((itm) => {
            return { id: itm.provider, Target_name: itm.name };
          });
        setInputData(data);

        setLoadingData(false);
      default:
        break;
    }
  };

  /**
   * Handles pagination changes: resets the filter panel's scroll position,
   * syncs the new page number to the URL, and updates state.
   */
  const handlePagination = (page) => {
    if (filterRef.current) {
      filterRef.current.scrollTop = GlobalConfiguration.lengthCheckZero;
    }
    handleSubmit("pageno", page);
    // setLabelAccounts([]);
    setCurrentPage(page);
  };

  /** Resets all filter/search state to its defaults. */
  const clearFilter = () => {
    setSearchData("");
    setTarget("");
    setStartDate("");
    setEndDate("");
    setCurrentStatus("");
    setCurrentLinkageStatus("");
    setStartDateFilter("");
    setEndDateFilter("");
    setChannelProvider("");
  };

  // Re-fetches the channel list whenever any filter, search term, or
  // pagination value changes.
  useEffect(() => {
    fetchData();
  }, [
    pageSize,
    selectedValue,
    searchData,
    target,
    startDate,
    endDate,
    currentStatus,
    currentPage,
    currentLinkageStatus,
    targetProvider,
    channelProvider,
    notificationId,
  ]);

  useEffect(() => {
    if (active?.length) {
      fetchFilterData();
    }
  }, [active]);

  // Scrolls the filter panel back to the top whenever the page, page size,
  // or accumulated data changes, so newly loaded results are visible.
  useEffect(() => {
    if (filterRef.current) {
      filterRef.current.scrollTo({
        top: GlobalConfiguration?.lengthCheckZero,
      });
    }
  }, [currentPage, pageSize]);

  return (
    <div
      className={`bg-primary-bg flex relative flex-col overflow-hidden ${popup ? "h-height-full" : ""}`}
    >
      <div
        className={`bg-primary-bg flex flex-col relative overflow-hidden h-screen`}
      >
        <div className="hidden Qsmd:flex flex-col">
          <div
            className={`relative  z-z-index-overlay ${direction === "down" ? "bg-primary-bg" : "bg-primary-nav"} `}
            style={{
              height: popup ? "" : "var(--navbar-breadcrumb-height)",
            }}
          ></div>
        </div>
        <div className="z-z-index-dropdown">
          <TopNavComponent
            direction={direction}
            topNavCtaBtnWithIcon1Props={
              popup && isMobile
                ? null
                : {
                    btnText: `${TEXT?.CREATE} ${TEXT?.CHANNEL.toLowerCase()}`,
                    btnType: "button",
                    handleFunction: () => {
                      if (loading) return;
                      if (popup) {
                        actionRestricted();
                        return;
                      }
                      if (hasAccess) {
                        navigate("/home/notification_dashboard/create_channel");
                      } else {
                        navigate("/feature_not_assigned", {
                          state: { unavailableService: "CChannel" },
                        });
                      }
                    },
                    isDisabled: popup || loading,
                    enabledTextColor: "var(--primary-bg)",
                    disabledTextColor: null,
                    enabledBgColor: "var(--text-primary)",
                    disabledBgColor: null,
                    isLoading: false,
                    outLineColor: null,
                    hoverOption: "black",
                    buttonRef: null,
                    fullWidthRequired: null,
                    inActiveVariantNeeded: null,
                    isLoaderNeeded: null,
                    icon: ICONS?.ADD_BUTTON_WHITE,
                    imgColor: "var(--primary-bg)",
                    iconToBeAlign: "right",
                  }
            }
            filteredSearchProps={{
              loadingData: loadingData,
              setShowInnerSearchBar: setShowInnerSearchBar,
              showSearchBar: showSearchBar,
              isSortDisabled: false,
              isFilterDisabled: loading,
              isSearchDisabled: loading,
              onSearchInput: (val) => {
                setSearchUserTextDebounce(val);
              },

              searchText: searchUserTextDebounce,
              searchPlaceholder: `${TEXT?.SEARCH} ${TEXT?.CHANNEL.toLowerCase()}`,
              filterPlaceholder: `${TEXT?.CHANNEL} ${TEXT?.NAME.toLowerCase()}`,
              filterItems: [
                {
                  name: TEXT?.SEARCH,
                  isMulti: false,
                  value: searchData,
                  setValue: (val) => {
                    handleSubmit("search", val);
                    setCurrentPage(GlobalConfiguration.lengthCheckOne);
                    setSearchData(val);
                  },
                  valueType: "string",
                  isDropdown: false,
                  isDisabled: loading,
                },
              ],
              filters: [
                {
                  name: `${TEXT?.STATUS}`,
                  filterType: "dropdown",
                  variant: "single",
                  size: "small",
                  value: currentStatus || "",
                  dropdownPlaceHolder: "Select status",
                  isDisabled: false,
                  options: ["Active", "Inactive"],
                  statusPop: true,

                  handleChange: handleStatus,
                },

                {
                  name: `${TEXT?.CHANNEL} ${TEXT?.LINKAGE.toLowerCase()}`,
                  filterType: "dropdown",
                  variant: "single",
                  size: "small",
                  value: currentLinkageStatus || "",
                  dropdownPlaceHolder: "Select status",
                  isDisabled: false,
                  options: ["Pending", "Success", "Failed"],
                  statusPop: true,

                  handleChange: handleLinkageStatus,
                },
                {
                  name: `${TEXT?.DATE_RANGE}`,
                  filterType: "calender",
                  variant: "range",
                  size: "small",
                  value:
                    (startDate && endDate) ||
                    (startDateFilter && endDateFilter),
                  isDisabled: false,
                  startDate:
                    startDate ||
                    (startDateFilter
                      ? new Date(
                          startDateFilter *
                            IntervalConfiguration.intervalMedium,
                        )
                      : null),
                  endDate:
                    endDate ||
                    (endDateFilter
                      ? new Date(
                          endDateFilter * IntervalConfiguration.intervalMedium,
                        )
                      : null),
                  handleChange: (date) => {
                    handleSubmit("date", date);
                    setCurrentPage(GlobalConfiguration.lengthCheckOne);
                    handleDateChange(date);
                  },
                },
                {
                  name: TEXT?.TARGET,
                  filterType: "dropdown",
                  variant: "single",
                  size: "small",
                  value: providerName || channelProvider || "",
                  dropdownPlaceHolder: `${TEXT?.SELECT} ${TEXT?.TARGET.toLowerCase()}`,
                  isDisabled: targetProvider ? true : false,
                  options: [
                    TEXT?.GOOGLE_CHAT,
                    TEXT?.MICROSOFT_TEAMS,
                    TEXT?.SLACK,
                    TEXT?.WEBHOOOK,
                  ],
                  statusPop: true,

                  handleChange: handleChannelProvider,
                },
              ],

              isFilterClearAllDisabled: !(
                (startDateFilter && endDateFilter) ||
                currentStatus !== "" ||
                currentLinkageStatus !== "" ||
                channelProvider !== "" ||
                loading
              ),
              isSearchClearAllDisabled: !(
                searchData.length ||
                target.length ||
                loading
              ),
              inputData: inputData,
              active: active,
              setActive: setActive,
              loadingData: loadingData,
              loading: loading,
              setLoading: setLoadingData,
              isShowSortBy: false,
              isShowFilter: true,
            }}
            ispagination={
              channelsViewAllData?.length <= GlobalConfiguration.lengthCheckZero
                ? false
                : true
            }
          />
        </div>

        {showInnerSearchBar && (
          <div className="absolute bg-PopUp-BackDrop opacity-dark-opacity w-full h-full z-z-index-header"></div>
        )}
        <div
          onScroll={handleScroll}
          ref={scrollRef}
          style={{
            scrollPaddingTop: "var(--padding-md)",
            scrollPaddingBottom: "var(--padding-md)",
            marginBottom:
              isMobile && !popup
                ? "var(--mobile-margin-popup-bottom-offset)"
                : "var(--padding-base)",
          }}
          className="scroll flex flex-col items-center min-h-0 h-full-height overflow-y-auto w-full"
        >
          {loading ? (
            <div className="loader-container m-auto flex justify-center items-center w-width-full h-height-full">
              <Loader />
            </div>
          ) : channelsViewAllData?.length >
            GlobalConfiguration.lengthCheckZero ? (
            <div className="flex flex-col w-width-full flex-1 min-h-height-base m-auto">
              <ChannelViewallCard
                setIsChannelViewallOpen={setIsChannelViewallOpen}
                filterRef={scrollRef}
                channelData={channelsViewAllData}
                showSearchBar={showSearchBar}
                clearFilter={clearFilter}
                isSideBarOpen={isSidebarOpen}
                setLoadingDetail={setLoadingDetail}
              />
            </div>
          ) : (
            // Empty state: shown when the current filters/search return no
            // channels. Only offers a "Clear parameters" action if at least
            // one filter or search value is actually active.
            <div
              className={`flex flex-col items-center justify-center m-auto w-full h-full`}
              style={{ overflowY: showSearchBar && "scroll" }}
            >
              <img
                src={ICONS?.NO_DATA_FOUND}
                alt="No data found"
                className="w-graphic-width-md h-graphic-rectangle-height-md mb-margin-sm"
              />
              <p className="bene-acc-no-beneaccount-text mb-margin-sm">
                {TEXT?.NO_DATA_FOUND}
              </p>
              {searchData?.length ||
              target?.length ||
              startDateFilter ||
              currentLinkageStatus.length ||
              currentStatus.length ||
              channelProvider.length ||
              endDateFilter ? (
                <PrimaryButton
                  enabledBgColor="var(--primary-nav-act)"
                  btnText={TEXT?.CLEAR_PARAMETERS}
                  btnType="button"
                  handleFunction={() => {
                    clearFilter();
                    handleClearSearchParam();
                  }}
                  enabledTextColor="var(--text-primary)"
                  hoverOption="white"
                />
              ) : null}
            </div>
          )}
        </div>
        {channelsViewAllData?.length > GlobalConfiguration.lengthCheckZero && (
          <div
            className={`w-width-full ${showInnerSearchBar ? "z-z-index-content" : "z-z-index-fixed-ui"} bg-primary-nav rounded-none
                
                ${
                  popup
                    ? "w-width-full Qmd:w-full_popup-width  rounded-none Qmd:rounded-b-border-radius-md Qmd:rounded-t-none"
                    : "w-width-full rounded-none"
                }
                `}
          >
            <div className="block">
              <PaginationComponent
                handleSelect={handleSelect}
                selectedValue={selectedValue}
                data={mainData}
                pageSize={pageSize}
                dropdownOptions={dropdownOptions}
                handlePagination={handlePagination}
                currentPage={currentPage}
                setIsOpen={setIsOpen}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default ChannelViewAll;
